import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import UILink from 'UIComponents/link/UILink';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import userInfo from 'hub-http/userInfo';
import debounce from 'transmute/debounce';
import HR from 'UIComponents/elements/HR';
import Small from 'UIComponents/elements/Small';
import UISelect from 'UIComponents/input/UISelect';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { BaseFilterLevels, FilterOptionTypes } from './Constants';
import getCustomFilterOptions from './CustomOptions';
import getTeamOptions from './TeamOptions';
import getUserOptions from './UserOptions';
import { getHasTeamsWriteScope } from '../../redux/selectors';
import { checkHasMore, filterOptions, getPaginatedOptions } from './OptionsPagination';
const IndentedTeamOption = styled.div.withConfig({
  displayName: "UsersAndTeamsSelect__IndentedTeamOption",
  componentId: "sc-1ipfia3-0"
})(["padding-left:", ";"], props => `${props.depth * 10}px`);

const UserAndTeamOptionComponent = (_ref) => {
  let {
    option,
    children
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["option", "children"]);

  // include a divider between the top "all" option and the final custom option "unassigned"
  if (option.value === BaseFilterLevels.ALL || option.value === BaseFilterLevels.UNASSIGNED) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("div", Object.assign({}, rest, {
        children: children
      })), /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      })]
    });
  }

  if (option.value === BaseFilterLevels.ASSIGNED_TO_USERS_TEAM && option.disabled) {
    return /*#__PURE__*/_jsx("span", Object.assign({}, rest, {
      children: /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.tooltips.noTeamAssignmentTooltip"
        }),
        children: children
      })
    }));
  }

  if (option.type === FilterOptionTypes.TEAM) {
    return /*#__PURE__*/_jsx(IndentedTeamOption, Object.assign({
      depth: option.depth
    }, rest, {
      children: children
    }));
  } // In order to support rendering the UISelect's checkbox's for the user item,
  // do not custom render the user option when multi is true.


  if (option.type === FilterOptionTypes.USER && option.multi) {
    return /*#__PURE__*/_jsx("div", Object.assign({}, rest, {
      children: children
    }));
  }

  if (option.type === FilterOptionTypes.USER) {
    const {
      className = ''
    } = rest,
          otherProps = _objectWithoutPropertiesLoose(rest, ["className"]);

    let ownedAssetCountSection = null;

    if (option.ownedAssetCount !== undefined) {
      ownedAssetCountSection = /*#__PURE__*/_jsxs(Small, {
        "data-test-id": "owned-asset-count",
        children: [option.ownedAssetCount, " ", option.assetTypeDisplayName]
      });
    }

    return /*#__PURE__*/_jsxs("div", Object.assign({
      className: `${className} align-center p-y-3`
    }, otherProps, {
      children: [option.avatar, /*#__PURE__*/_jsxs("div", {
        className: "m-left-4",
        children: [/*#__PURE__*/_jsx("div", {
          children: option.text
        }), ownedAssetCountSection]
      })]
    }));
  }

  return /*#__PURE__*/_jsx("div", Object.assign({}, rest, {
    children: children
  }));
};

const UsersAndTeamsSelect = (_ref2) => {
  let {
    requestsLoading = false,
    requestsError = false,
    users,
    teams,
    allowPrivate = true,
    assetTypeDisplayName,
    userHasTeamAssignmentScope = true,
    onChange,
    showUnassigned = true,
    showAny = true,
    customTeamOptionDisabled = false,
    additionalOptions = [],
    multi,
    menuWidth = 'auto',
    showMyTeamsOption = true,
    showFooter = true
  } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, ["requestsLoading", "requestsError", "users", "teams", "allowPrivate", "assetTypeDisplayName", "userHasTeamAssignmentScope", "onChange", "showUnassigned", "showAny", "customTeamOptionDisabled", "additionalOptions", "multi", "menuWidth", "showMyTeamsOption", "showFooter"]);

  const [ownerInfo, setOwnerInfo] = useState({});
  const [portalId, setPortalId] = useState(0);
  const [userScopes, setUserScopes] = useState([]);
  const hasWriteScope = getHasTeamsWriteScope(userScopes);
  const {
    footerLink,
    footerMessage
  } = useMemo(() => {
    const hasUsers = users && users.length > 0;
    const hasTeams = teams && teams.length > 0;

    if (hasUsers && hasTeams) {
      return {
        footerLink: `/settings/${portalId}/users`,
        footerMessage: 'ui-asset-management-lib.footer.manageUsersAndTeams'
      };
    } else if (hasUsers) {
      return {
        footerLink: `/settings/${portalId}/users`,
        footerMessage: 'ui-asset-management-lib.footer.manageUsers'
      };
    } else if (hasTeams) {
      return {
        footerLink: `/settings/${portalId}/users/teams`,
        footerMessage: 'ui-asset-management-lib.footer.manageTeams'
      };
    }

    return {
      footerLink: '',
      footerMessage: ''
    };
  }, [portalId, teams, users]);
  const shouldRenderFooter = useMemo(() => Boolean(showFooter && hasWriteScope && footerMessage !== ''), [footerMessage, hasWriteScope, showFooter]);
  useEffect(() => {
    userInfo().then(authData => {
      setOwnerInfo(authData.user);
      setUserScopes(authData.user.scopes || []);
      setPortalId(authData.portal ? authData.portal.portal_id : 0);
    }).catch(() => {
      setOwnerInfo({});
    });
  }, []);

  if (requestsLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {});
  }

  if (requestsError) {
    // TODO: What should we do on error?
    return null;
  }

  const customOptions = getCustomFilterOptions({
    allowPrivate,
    assetTypeDisplayName,
    userHasTeamAssignmentScope,
    showUnassigned,
    showAny,
    customTeamOptionDisabled,
    additionalOptions,
    ownerInfo,
    showMyTeamsOption
  });
  const teamOptions = getTeamOptions({
    teams
  });
  const userOptions = getUserOptions({
    users,
    assetTypeDisplayName,
    userHasTeamAssignmentScope,
    multi
  });

  const handleLoadOptions = (input, callback, pagination = {
    offset: 0,
    hasMore: false,
    prevInput: '',
    filteredCustomOptions: customOptions,
    filteredTeamOptions: teamOptions,
    filteredUserOptions: userOptions
  }) => {
    const PAGE_SIZE = 30;
    let hasMore = false;
    const inputChanged = input !== pagination.prevInput;
    let nextOffset = !inputChanged ? pagination.offset : 0;
    let nextOptions = [];
    let filteredCustomOptions = pagination.filteredCustomOptions;
    let filteredTeamOptions = pagination.filteredTeamOptions;
    let filteredUserOptions = pagination.filteredUserOptions;

    if (!input) {
      filteredCustomOptions = customOptions;
      filteredTeamOptions = teamOptions;
      filteredUserOptions = userOptions;
    } else if (inputChanged) {
      filteredCustomOptions = filterOptions(customOptions, input);
      filteredTeamOptions = [Object.assign({}, teamOptions[0], {
        options: filterOptions(teamOptions[0].options, input)
      })];
      filteredUserOptions = [Object.assign({}, userOptions[0], {
        options: filterOptions(userOptions[0].options, input)
      })];
    }

    nextOffset = nextOffset + PAGE_SIZE;
    nextOptions = getPaginatedOptions(filteredCustomOptions, filteredTeamOptions, filteredUserOptions, nextOffset);
    hasMore = checkHasMore(nextOptions, filteredCustomOptions, filteredTeamOptions, filteredUserOptions);
    callback(null, {
      options: nextOptions,
      pagination: {
        hasMore,
        offset: nextOffset,
        prevInput: input,
        filteredCustomOptions,
        filteredTeamOptions,
        filteredUserOptions
      }
    });
  };

  return /*#__PURE__*/_jsx(UISelect, Object.assign({
    "data-test-id": "users-and-teams-select",
    loadOptions: debounce(600, handleLoadOptions),
    buttonUse: "transparent",
    menuWidth: menuWidth,
    optionComponent: UserAndTeamOptionComponent,
    defaultValue: BaseFilterLevels.ALL,
    multi: multi,
    searchPlaceholder: I18n.text('ui-asset-management-lib.searchUsersAndTeams'),
    onSelectedOptionChange: ({
      target
    }) => {
      const val = target.value;
      const output = multi ? val.map(elmnt => ({
        id: elmnt.value,
        type: elmnt.type
      })) : {
        id: val.value,
        type: val.type
      };
      onChange(output);
    },
    dropdownFooter: shouldRenderFooter && /*#__PURE__*/_jsx(UILink, {
      "data-test-id": "users-and-teams-select-dropdown-footer",
      external: true,
      href: footerLink,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: footerMessage
      })
    })
  }, rest));
};

UsersAndTeamsSelect.propTypes = {
  // The display name of the asset (e.g. landing pages, workflows, ect.)
  // * Name should always be pluralized
  assetTypeDisplayName: PropTypes.string.isRequired,
  // The teams that can be filtered. should be passed in hierarchical format.
  // - It should only show teams that the user can see/has access to
  // - It should not include teams that have no assets assigned to them.
  teams: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    childTeams: PropTypes.array,
    parentTeamId: PropTypes.number,
    userIds: PropTypes.arrayOf(PropTypes.number),
    secondaryUserIds: PropTypes.arrayOf(PropTypes.number)
  })).isRequired,
  // Should not be all the users in the portal, instead it should include
  // - active users in the portal who have at least 1 asset assigned to them
  // - removed users as well if they have at least 1 asset assigned to them
  // ** Technically not required if the user does not have userHasTeamAssignmentScope
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    // An optional count of the number of assets assigned to the user. Will
    // not be displayed if the value is undefined.
    ownedAssetCount: PropTypes.number
  })).isRequired,
  // Used to indicate whether or not requests for either users or teams are
  // loading. When true, the dropdown will show a loading state.
  requestsLoading: PropTypes.bool.isRequired,
  // Used to indicate whether or not requests for either users or teams have
  // failed. When true, the dropdown will show a failure state.
  requestsError: PropTypes.bool.isRequired,
  // Optional: If both allowPrivate and userHasTeamAssignmentScope are true,
  // the dropdown will include a `<name> (Me)` option.
  allowPrivate: PropTypes.bool,
  // Optional: If both allowPrivate and userHasTeamAssignmentScope are true,
  // the dropdown will include a `<name> (Me)` option. If showAny is true and
  // userHasTeamAssignmentScope is true, the dropdown will include an `Any`
  // option. If showAny is true and userHasTeamAssignmentScope is false, the
  // dropdown will include an `All Teams` option.
  userHasTeamAssignmentScope: PropTypes.bool,
  // Called when filter changes, includes the type of filter selected
  // (user/team/custom, and the ID).
  onChange: PropTypes.func.isRequired,
  // Optional: If true, the dropdown will included the
  // `Unassigned <assetTypeDisplayName>` option.
  showUnassigned: PropTypes.bool,
  // Optional: If showAny is true and userHasTeamAssignmentScope is true,
  // the dropdown will include an `Any` option. If showAny is true and
  // userHasTeamAssignmentScope is false, the dropdown will include an
  // `All Teams` option.
  showAny: PropTypes.bool,
  // Optional: If true, the dropdown will support multiple selection.
  // NOTE: If using multi, the implementing team will have to pass in a value prop
  // of the selected values as it updates.
  // Example: https://private.hubteam.com/opengrok/xref/all/git.hubteam.com-HubSpot-ContentEmailUI%23master/static/js/components/manage/ExtraFilters.js#11
  multi: PropTypes.bool,
  // Optional: Sets the width of the dropdown.
  menuWidth: PropTypes.oneOf(['auto']),
  // Optional: Adds extra options to the dropdown.
  additionalOptions: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    disabled: PropTypes.bool
  })),
  // Optional: If true, the dropdown will include the `My Teams` option.
  showMyTeamsOption: PropTypes.bool,
  // Optional: If true, disables the `My Teams` option. Set this if the user
  // is not assigned to any teams.
  customTeamOptionDisabled: PropTypes.bool,
  // Optional: If true, will render a "Manage teams" or "Manage users" link as a footer in UISelect"
  showFooter: PropTypes.bool
};
UsersAndTeamsSelect.defaultProps = {
  showAny: true,
  allowPrivate: true,
  requestsError: false,
  requestsLoading: false,
  userHasTeamAssignmentScope: true,
  showUnassigned: true,
  customTeamOptionDisabled: false,
  additionalOptions: [],
  menuWidth: 'auto',
  showMyTeamsOption: true,
  showFooter: true
};
export default UsersAndTeamsSelect;